import { useQuery } from '@tanstack/react-query';
import type { Control, FieldValues, Path } from 'react-hook-form';
import { FormControl, FormField, FormItem, FormMessage } from 'shared/ui/form';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'shared/ui/select';
import { allTariffs } from '@/enteties/tariff/api';
import * as React from 'react';
import type { IServiceSelect } from 'widgets/project-creation-stepper/model';
import { useMemo } from 'react';
import _ from 'lodash';
import { useOnError } from 'shared/hooks/useOnError';
import { TariffSelectorSkeleton } from 'features/change-tariff/ui/tariff-selector skeleton';
import * as m from '@paraglide/messages';
export interface TariffFieldProps {
  serviceType: IServiceSelect['name'];
}
interface TariffSelectorProps<TData extends FieldValues> extends TariffFieldProps {
  name: Path<TData>;
  control: Control<TData> | undefined;
}
export const TariffSelector = <TData extends FieldValues,>({
  name,
  control,
  serviceType
}: TariffSelectorProps<TData>) => {
  const tariffs = useQuery({
    ...allTariffs.getAllTariffsQueryOptions()
  });
  useOnError({
    isError: tariffs.isError,
    description: tariffs.error?.message
  });
  const tariffsData = useMemo(() => {
    if (!tariffs.data) return [];
    if (serviceType === 'marketplace' || serviceType === 'db') {
      const tariffsClone = _.cloneDeep(tariffs.data);
      _.remove(tariffsClone, tariff => tariff.id === 4);
      return tariffsClone;
    }
    return tariffs.data;
  }, [serviceType, tariffs.data]);
  const changeTariff = (tariffId: string, fieldChange: (tariffId: string) => void) => {
    if (!tariffId) return;
    fieldChange(tariffId);
  };
  if (tariffs.isLoading) return <TariffSelectorSkeleton />;
  return <div className="flex-2 w-full" data-sentry-component="TariffSelector" data-sentry-source-file="tariff-selector.tsx">
      <FormField name={name} control={control} rules={{
      maxLength: 12,
      required: m.required_field()
    }} render={({
      field
    }) => <FormItem className="flex flex-col">
            <Select onValueChange={value => changeTariff(value, field.onChange)} value={field.value}>
              <FormControl>
                <SelectTrigger>
                  <SelectValue placeholder={m.tab_tariff_field_plan_placeholder()} />
                </SelectTrigger>
              </FormControl>
              <SelectContent>
                {tariffsData.map(tariff => <SelectItem key={tariff.id} value={tariff.id.toString()}>
                    {tariff.name}
                  </SelectItem>)}
              </SelectContent>
            </Select>
            <FormMessage />
            <p className="pl-4 text-caption leading-4 text-muted-foreground mt-2">
              {tariffsData.find(
        // eslint-disable-next-line no-unsafe-optional-chaining
        tariff => tariff.id === +field?.value)?.description ?? null}
            </p>
          </FormItem>} data-sentry-element="FormField" data-sentry-source-file="tariff-selector.tsx" />
    </div>;
};